import { apiGet, apiPost } from './apiClient';

// Plan actions for sign-up plan selection and Plan & billing.

// Starts the free trial (full access, so no plan is sent). Safe to repeat: if a trial is already
// running the backend reports it rather than restarting it. Resolves to { startedTrial, profile };
// throws (status 409) when the account can't have a trial, e.g. it already had one.
export async function startTrial() {
  return apiPost('/trial/start', {});
}

// Sign-up records the trial choice on the plan screen, but the trial only starts once onboarding
// is finished ("Get Started" on Servings, the last screen), so someone who backs out part-way
// never starts one. sessionStorage: survives a reload mid-onboarding, gone when the tab closes.
const ONBOARDING_TRIAL_KEY = 'mp:onboardingTrialChosen';

export function chooseTrialForOnboarding() {
  sessionStorage.setItem(ONBOARDING_TRIAL_KEY, '1');
}

export function clearTrialChoice() {
  sessionStorage.removeItem(ONBOARDING_TRIAL_KEY);
}

// Call when onboarding finishes. Starts the trial if one was chosen; resolves to null if not.
// A 409 (this account can't have a trial) is not worth blocking onboarding over, so it is
// swallowed; any other failure is thrown so the screen can ask the user to try again.
export async function startChosenTrial() {
  if (sessionStorage.getItem(ONBOARDING_TRIAL_KEY) !== '1') return null;
  try {
    const result = await startTrial();
    sessionStorage.removeItem(ONBOARDING_TRIAL_KEY);
    return result;
  } catch (err) {
    if (err?.status === 409) {
      sessionStorage.removeItem(ONBOARDING_TRIAL_KEY);
      return null;
    }
    throw err;
  }
}

// A message safe to show inline for a failed billing request. The server's own words when it gave
// any (e.g. "You already have a subscription…", or the 503 "Payments are temporarily
// unavailable…"), otherwise the caller's fallback.
export function billingErrorMessage(err, fallback) {
  const payload = err?.payload || {};
  if (typeof payload.message === 'string' && payload.message) return payload.message;
  if ((err?.status === 409 || err?.status === 400) && typeof payload.error === 'string' && payload.error) return payload.error;
  return fallback;
}

// Opens Stripe Checkout for { plan, interval }. The browser leaves the app here, so this only
// resolves if something went wrong before the redirect (it throws then). `returnTo` picks the
// screen Checkout's back link returns to: 'onboarding' (sign-up plan screen) or 'plans'.
// The server decides the free days (the trial is honoured; first charge at its end) and refuses
// anyone who already has a plan.
export async function startCheckout({ plan, interval, returnTo = 'plans' }) {
  const { url } = await apiPost('/billing/checkout', { plan, interval, returnTo });
  if (!url) throw new Error('Checkout did not return an address');
  window.location.assign(url);
  return { status: 'redirecting' };
}

// After Checkout: { state: 'complete' | 'processing' | 'open' | 'expired', profile? }.
export async function getCheckoutStatus(sessionId) {
  return apiGet(`/billing/checkout/${encodeURIComponent(sessionId)}`);
}

// During the free trial nothing has been charged, so changing plan or interval is instant and
// free. Resolves to { profile }.
export async function changeTrialPlan({ plan, interval }) {
  return apiPost('/billing/trial-plan', { plan, interval });
}

// Placeholders for paid plans until later Stripe phases. Each resolves to status 'coming-soon' and
// the screen shows an inline "coming soon" message.
// TODO(Stripe phase 4/5): upgrade = immediate, prorated; downgrade and interval switches = at renewal.
export async function changePlan({ plan, interval }) {
  return { plan, interval, status: 'coming-soon' };
}

// Stripe's customer portal. flow 'manage': card details and invoices. flow 'cancel': straight into
// cancelling (it runs to the end of the period). The browser leaves the app; Stripe brings it back
// to Plan & billing with ?from=portal, which then refreshes the plan.
export async function openBillingPortal({ flow = 'manage' } = {}) {
  const { url } = await apiPost('/billing/portal', { flow });
  if (!url) throw new Error('The billing portal did not return an address');
  window.location.assign(url);
  return { status: 'redirecting' };
}

// "Keep my plan": undo a pending cancellation. Resolves to { profile }.
export async function resumeSubscription() {
  return apiPost('/billing/resume', {});
}

// Bring the plan up to date from Stripe (after returning from the portal). Resolves to { profile }.
export async function refreshBilling() {
  return apiPost('/billing/refresh', {});
}

// Upgrade Solo -> Family. The preview is what would be charged today: { amountDue (cents),
// currency, prorationDate, renewalDate, intervalChange }. Pass its prorationDate to upgradePlan
// so the charge matches what was shown.
export async function previewUpgrade({ interval }) {
  return apiPost('/billing/upgrade/preview', { interval });
}

// Charged now and applied only if the payment goes through. Resolves to
// { status: 'upgraded', profile } or { status: 'requires_action', url } (the bank wants the
// person to confirm on Stripe's page). A declined card throws with status 402.
export async function upgradePlan({ interval, prorationDate }) {
  return apiPost('/billing/upgrade', { interval, ...(prorationDate ? { prorationDate } : {}) });
}
